import { CheckCircle, Shield, AlertCircle, Info } from 'lucide-react';
import { useState } from 'react';

interface VerificationBadgeProps {
  type: 'vaccination' | 'age-eligibility' | 'weight-eligibility' | 'allergy-presence' | 'medication-status' | 'license-valid';
  verified: boolean;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

export function VerificationBadge({ type, verified, size = 'md', showTooltip = true }: VerificationBadgeProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const labels = {
    'vaccination': 'Vaccinated',
    'age-eligibility': 'Age Eligible',
    'weight-eligibility': 'Weight Eligible',
    'allergy-presence': 'Allergies',
    'medication-status': 'Medication',
    'license-valid': 'Licensed & Valid'
  };

  const icons = {
    'vaccination': '💉',
    'age-eligibility': '🎂',
    'weight-eligibility': '⚖️',
    'allergy-presence': '🚨',
    'medication-status': '💊',
    'license-valid': '🏥'
  };

  const descriptions = {
    'vaccination': 'Vaccination status confirmed without accessing medical records',
    'age-eligibility': 'Age requirement met without revealing date of birth',
    'weight-eligibility': 'Weight requirement met without sharing actual weight',
    'allergy-presence': 'Allergy status indicated without listing specific allergies',
    'medication-status': 'Medication status confirmed without revealing details',
    'license-valid': 'Professional credentials verified without exposing documents'
  };

  const sizeClasses = {
    sm: { badge: 'px-2 py-1 text-xs', icon: 'w-3 h-3', text: 'text-xs' },
    md: { badge: 'px-3 py-1.5 text-sm', icon: 'w-4 h-4', text: 'text-sm' },
    lg: { badge: 'px-4 py-2 text-base', icon: 'w-5 h-5', text: 'text-base' }
  };

  const classes = sizeClasses[size];

  if (!verified) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 rounded-full border-2 border-gray-300 bg-gray-50 ${classes.badge}`}
        style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
      >
        <AlertCircle className={classes.icon} />
        <span>Not Verified</span>
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <div
        className={`inline-flex items-center gap-1.5 rounded-full border-2 border-green-300 bg-green-50 ${classes.badge} cursor-help`}
        style={{ color: '#059669', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <CheckCircle className={classes.icon} />
        <span>{labels[type]}</span>
      </div>

      {/* Tooltip */}
      {showTooltip && tooltipVisible && (
        <div
          className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white rounded-lg p-3 shadow-xl"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex items-start gap-2 mb-2">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="text-xs font-semibold">Privacy-Protected Verification</div>
          </div>
          <p className="text-xs opacity-90">{descriptions[type]}</p>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
}

interface VerificationStatusProps {
  verifications: Array<{
    type: 'vaccination' | 'age-eligibility' | 'weight-eligibility' | 'allergy-presence' | 'medication-status' | 'license-valid';
    verified: boolean;
  }>;
  layout?: 'horizontal' | 'vertical';
}

export function VerificationStatus({ verifications, layout = 'horizontal' }: VerificationStatusProps) {
  const containerClass = layout === 'horizontal' ? 'flex flex-wrap gap-2' : 'space-y-2';

  return (
    <div className={containerClass}>
      {verifications.map((v, index) => (
        <VerificationBadge key={index} type={v.type} verified={v.verified} size="md" />
      ))}
    </div>
  );
}

interface DataStateIndicatorProps {
  state: 'full-access' | 'verified' | 'hidden';
  label?: string;
}

export function DataStateIndicator({ state, label }: DataStateIndicatorProps) {
  const states = {
    'full-access': {
      icon: '🔓',
      text: 'Full Access',
      color: '#3B82F6',
      bg: '#DBEAFE',
      description: 'Data shared with consent'
    },
    'verified': {
      icon: '✅',
      text: 'Verified',
      color: '#059669',
      bg: '#D1FAE5',
      description: 'Condition met (privacy-protected)'
    },
    'hidden': {
      icon: '🔒',
      text: 'Hidden',
      color: '#6B7280',
      bg: '#F3F4F6',
      description: 'No access'
    }
  };

  const config = states[state];

  return (
    <div className="inline-flex items-center gap-2">
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
        style={{ backgroundColor: config.bg, color: config.color, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
      >
        <span>{config.icon}</span>
        <span>{label || config.text}</span>
      </div>
    </div>
  );
}
